import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import { useState } from "react";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from "../config";
import { useDispatch } from "react-redux";
import { setAccess, setMe, resetSessionPolicy } from "../redux/sessionSlice";
import logo from '../assets/valonix-logo-siyah.png';
import { useLocation, useNavigate } from "react-router-dom";
import { getMe } from "../api/authApi";
import { useTranslation } from "react-i18next";
import '../App.css';

const NAVY = '#1a365d';
const NAVY_HOVER = '#2c5282';

function Copyright(props) {
    return (
        <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: '0.65rem' }} {...props}>
            {'Copyright © '}
            <Link color="inherit" href="#" underline="hover">
                VALONIX
            </Link>{' '}
            {new Date().getFullYear()}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: NAVY,
            dark: NAVY_HOVER,
        },
        secondary: {
            main: NAVY_HOVER,
        },
    },
    typography: {
        fontSize: 13,
    },
    shape: {
        borderRadius: 2,
    },
});

export default function Login(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [otpStep, setOtpStep] = useState(false);
    const [loginForm, setLoginform] = useState({
        username: "",
        password: "",
    });

    const onChangeForm = (label, event) => {
        switch (label) {
            case "username":
                setLoginform({ ...loginForm, username: event.target.value });
                break;
            case "password":
                setLoginform({ ...loginForm, password: event.target.value });
                break;
            default:
                break;
        }
    };

    const formatLoginError = (error) => {
        const detail = error?.response?.data?.detail;
        if (typeof detail === "string") return detail;
        if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.message) return detail.message;
        if (Array.isArray(detail)) {
            return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
        }
        const status = error?.response?.status;
        if (status === 405) {
            return t("auth.login.error.405");
        }
        if (!error?.response) {
            return t("auth.login.error.noResponse");
        }
        return t("auth.login.error.failed");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const fd = new FormData(event.currentTarget);
        const username = (fd.get("username") || loginForm.username || "").toString().trim();
        const password = (fd.get("password") ?? loginForm.password ?? "").toString();
        if (!username || !password) {
            toast.error(t("auth.login.error.required"));
            return;
        }
        const body = new URLSearchParams();
        body.set("username", username);
        body.set("password", password);
        const otp = (fd.get("otp") || "").toString().trim();
        if (otp) body.set("otp", otp);

        try {
            const response = await axios.post(`${API_URL}/auth/token`, body, {
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                withCredentials: true,
            });

            const contentType = response.headers?.["content-type"] || "";
            if (contentType.includes("text/html")) {
                toast.error(t("auth.login.error.html"));
                return;
            }

            const token = response.data?.access_token;
            const exp = response.data?.exp;
            if (!token) {
                toast.error(
                    response.data?.detail
                        ? String(response.data.detail)
                        : t("auth.login.error.noToken")
                );
                return;
            }

            dispatch(resetSessionPolicy());
            dispatch(setAccess({ token, exp }));

            try {
                const meData = await getMe(token);
                dispatch(setMe(meData));
                localStorage.setItem("user_info", JSON.stringify({
                    full_name: meData.full_name || "",
                    username: meData.username || "",
                }));
            } catch (e) {
                console.error("Kullanıcı bilgisi alınamadı:", e);
            }
            toast.success(t("auth.login.success"));
            // QR label deep link (valonix.io/d/<serial> -> /device/<serial>): after signing in, land on that device.
            // Only this one internal path shape is honoured, so the URL cannot be used as an open redirect.
            navigate(/^\/device\/VAL-\d{4}-\d{4}$/.test(location.pathname) ? location.pathname : "/home");
        } catch (error) {
            console.log(error);
            if (error?.response?.data?.detail?.code === "otp_required") {
                setOtpStep(true);  // the password was right: ask for the second factor
                toast.info(t("auth.login.otpPrompt"));
                return;
            }
            toast.error(formatLoginError(error));
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: NAVY,
                    padding: 1,
                    boxSizing: 'border-box',
                }}
            >
                <CssBaseline />
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 340,
                        padding: { xs: 2, sm: 2.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        border: '1px solid rgba(255,255,255,0.15)',
                    }}
                >
                    <Box
                        component="img"
                        src={logo}
                        alt={t("auth.logoAlt")}
                        sx={{
                            width: 'auto',
                            maxWidth: 120,
                            height: 32,
                            objectFit: 'contain',
                            marginBottom: 1,
                        }}
                    />

                    <Typography
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            color: NAVY,
                            marginBottom: 0.25,
                            textAlign: 'center',
                            lineHeight: 1.3,
                        }}
                    >
                        {t("auth.login.title")}
                    </Typography>

                    <Typography
                        sx={{
                            color: '#64748b',
                            fontSize: '0.72rem',
                            marginBottom: 1.5,
                            textAlign: 'center',
                        }}
                    >
                        {t("auth.login.subtitle")}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                        <TextField
                            margin="dense"
                            size="small"
                            required
                            fullWidth
                            id="username"
                            label={t("auth.login.username")}
                            name="username"
                            autoComplete="username"
                            autoFocus
                            onChange={(event) => onChangeForm("username", event)}
                            sx={{
                                '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                                '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 },
                                '& .MuiOutlinedInput-root': { borderRadius: 1 },
                            }}
                        />
                        <TextField
                            margin="dense"
                            size="small"
                            required
                            fullWidth
                            name="password"
                            label={t("auth.login.password")}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            onChange={(event) => onChangeForm("password", event)}
                            sx={{
                                '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                                '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 },
                                '& .MuiOutlinedInput-root': { borderRadius: 1 },
                            }}
                        />

                        {otpStep && (
                            <TextField
                                margin="dense"
                                size="small"
                                required
                                fullWidth
                                name="otp"
                                id="otp"
                                label={t("auth.login.otpLabel")}
                                autoComplete="one-time-code"
                                autoFocus
                                inputProps={{ inputMode: "text" }}
                                sx={{
                                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                                    '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 },
                                    '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                            />
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disableElevation
                            sx={{
                                mt: 1.5,
                                mb: 1,
                                py: 0.75,
                                borderRadius: 1,
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                backgroundColor: NAVY,
                                '&:hover': {
                                    backgroundColor: NAVY_HOVER,
                                },
                            }}
                        >
                            {t("auth.login.submit")}
                        </Button>

                        <Button
                            type="button"
                            fullWidth
                            variant="outlined"
                            sx={{
                                mb: 0.5,
                                py: 0.75,
                                borderRadius: 1,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                borderColor: NAVY,
                                color: NAVY,
                                borderWidth: 1,
                                '&:hover': {
                                    borderWidth: 1,
                                    borderColor: NAVY_HOVER,
                                    backgroundColor: 'rgba(15, 39, 68, 0.04)',
                                },
                            }}
                            onClick={() => navigate('/forgot-password')}
                        >
                            {t("auth.login.forgot")}
                        </Button>
                    </Box>

                    <Copyright sx={{ mt: 1.5 }} />
                </Paper>
            </Box>
        </ThemeProvider>
    );
}